// SideNavigation.js
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Truck, Package, TrendingUp, Bell, Settings,
  User, LogOut, BarChart, FileText, Users, Shield,
  ChevronLeft, Menu, DollarSign, Home, Target,
  MessageSquare, X
} from 'lucide-react';
import { fetchUnreadCount } from '../store/slices/notificationSlice';
import { logoutUser } from '../store/slices/authSlice';
import useScreenSize from '../hooks/useScreenSize'; // We'll create this hook
import { selectTotalUnreadCount } from '../store/slices/messagingSlice'; // Add this import

const NavItem = ({ icon: Icon, label, to, onClick, sidebarOpen, badge, isNotification }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  // Determine the icon's class based on active state and notification status
  let iconClass = 'h-5 w-5 transition-colors duration-200';
  if (sidebarOpen) {
    iconClass += ' mr-3';
  }
  if (isActive) {
    iconClass += ' text-blue-500';
  } else if (isNotification && !sidebarOpen && badge > 0) {
    iconClass += ' text-red-500';
  }

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  const content = (
    <div className={`relative flex items-center px-4 py-3 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-all duration-200 ${
      !sidebarOpen && 'justify-center'
    } ${
      isActive ? 'bg-blue-50 text-blue-600 font-medium border-r-2 border-blue-500' : ''
    }`}>
      <Icon className={iconClass} />
      {sidebarOpen && <span className="flex-grow">{label}</span>}
      {sidebarOpen && badge > 0 && (
        <motion.span 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[20px] text-center"
        >
          {badge > 99 ? '99+' : badge}
        </motion.span>
      )}
      {!sidebarOpen && badge > 0 && (
        <motion.span 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
        >
          {badge > 9 ? '9+' : badge}
        </motion.span>
      )}
    </div>
  );

  if (to) {
    return (
      <Link to={to} onClick={handleClick}>
        {content}
      </Link>
    );
  }

  return (
    <button onClick={handleClick} className="w-full text-left">
      {content}
    </button>
  );
};

const SideNavigation = ({ isOpen, onToggle }) => {
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const user = useSelector((state) => state.auth.user);
  const unreadNotificationsCount = useSelector(state => state.notifications.unreadCount);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Fetch initial unread count on mount
  // Note: Real-time updates are handled by WebSocket event handlers in eventHandlers.js
  // which dispatch addNotification for events like new_bid, load_status_update, etc.
  useEffect(() => {
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  // Handle body scroll lock when sidebar is open on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, isOpen]);

  const isCustomer = user?.role?.startsWith('customer');
  const isAdmin = user?.role?.endsWith('admin');

  // Add handleLogout function
  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  // Handle backdrop click to close sidebar on mobile
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && (isMobile || isTablet)) {
      onToggle();
    }
  };

  // Replace the existing unreadMessagesCount calculation with the selector
  const unreadMessagesCount = useSelector(selectTotalUnreadCount);

  return (
    <>
      {/* Backdrop overlay for mobile */}
      <AnimatePresence>
        {isOpen && (isMobile || isTablet) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={handleBackdropClick}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          x: isOpen ? 0 : (isMobile || isTablet) ? -280 : 0,
          width: isOpen ? 256 : (isMobile || isTablet) ? 256 : 64
        }}
        transition={{ 
          type: "spring", 
          stiffness: 300, 
          damping: 30,
          duration: 0.3
        }}
        className={`bg-white/95 backdrop-blur-sm shadow-2xl transition-all duration-300 ease-in-out fixed inset-y-0 z-50 flex flex-col border-r border-gray-200/50 ${
          isMobile || isTablet ? 'left-0 w-64' : 'left-0'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Enhanced Header */}
        <div className="p-4 flex justify-between items-center shrink-0 border-b border-gray-200/50 bg-white/80">
          <AnimatePresence mode="wait">
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="flex items-center space-x-3"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Package className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-lg text-gray-800">
                    {isCustomer ? 'Customer' : 'Carrier'}
                  </h2>
                  <p className="text-xs text-gray-500">Dashboard</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <motion.button 
            onClick={onToggle} 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            {isOpen ? (
              <X className="h-5 w-5 text-gray-600" />
            ) : (
              <Menu className="h-5 w-5 text-gray-600" />
            )}
          </motion.button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col overflow-y-auto">
          <div className="flex-1 flex flex-col py-2">
            <NavItem 
              icon={Home} 
              label="Home" 
              to={`/${isCustomer ? 'customer' : 'carrier'}/dashboard`} 
              sidebarOpen={isOpen} 
            />
            {isCustomer ? (
              <>
                <NavItem icon={Package} label="Shipments" to="/customer/shipments" sidebarOpen={isOpen} />
                <NavItem icon={Truck} label="Tracking" to="/customer/tracking" sidebarOpen={isOpen} />
                {!user?.isViewOnly && (
                  <NavItem icon={DollarSign} label="Billings" to="/customer/billings" sidebarOpen={isOpen} />
                )}
              </>
            ) : (
              <>
                <NavItem icon={Package} label="My Loads" to="/carrier/loads" sidebarOpen={isOpen} />
                <NavItem icon={Target} label="Tracking" to="/carrier/tracking" sidebarOpen={isOpen} />
                <NavItem icon={DollarSign} label="Billings" to="/carrier/billings" sidebarOpen={isOpen} />
                <NavItem 
                  icon={Truck} 
                  label="Vehicles" 
                  to="/carrier/vehicles" 
                  sidebarOpen={isOpen} 
                />
              </>
            )}
            <NavItem icon={BarChart} label="Analytics" to={`/${isCustomer ? 'customer' : 'carrier'}/analytics`} sidebarOpen={isOpen} />
            <NavItem 
              icon={MessageSquare} 
              label="Messages" 
              to={`/${isCustomer ? 'customer' : 'carrier'}/messages`} 
              sidebarOpen={isOpen}
              badge={unreadMessagesCount}
              isNotification={true}
            />
            <NavItem 
              icon={Bell} 
              label="Notifications" 
              to={`/${isCustomer ? 'customer' : 'carrier'}/notifications`} 
              sidebarOpen={isOpen}
              badge={unreadNotificationsCount > 0 ? unreadNotificationsCount : null}
              isNotification={true}
            />
            {isAdmin && (
              <motion.div
                initial={isOpen ? "open" : "closed"}
                animate={isOpen ? "open" : "closed"}
                variants={{
                  open: { opacity: 1, height: "auto" },
                  closed: { opacity: 0, height: 0 }
                }}
                transition={{ duration: 0.2 }}
              >
                {isOpen && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase mt-4 border-t border-gray-200/50 bg-gray-50/50"
                  >
                    Admin Panel
                  </motion.div>
                )}
                {isCustomer ? (
                  <NavItem icon={Users} label="User Management" to="/customer/user-management" sidebarOpen={isOpen} />
                ) : (
                  <>
                    <NavItem icon={Users} label="Driver Management" to="/carrier/drivers" sidebarOpen={isOpen} />
                    <NavItem icon={Users} label="Dispatcher Management" to="/carrier/dispatcher-management" sidebarOpen={isOpen} />
                  </>
                )}
              </motion.div>
            )}
          </div>

          {/* Enhanced Footer */}
          <div className="shrink-0 border-t border-gray-200/50 bg-white/80">
            <NavItem 
              icon={Settings} 
              label="Settings" 
              to={`/${isCustomer ? 'customer' : 'carrier'}/settings`} 
              sidebarOpen={isOpen} 
            />
            <NavItem 
              icon={LogOut} 
              label="Logout" 
              onClick={handleLogout} 
              sidebarOpen={isOpen} 
            />
            {isOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="px-4 py-3 text-xs text-gray-400 border-t border-gray-200/50"
              >
                <p className="truncate">
                  {user?.full_name || user?.name || 'User'}
                </p>
                <p className="truncate text-gray-500">
                  {user?.email}
                </p>
              </motion.div>
            )}
          </div>
        </nav>
      </motion.aside>
    </>
  );
};

export default SideNavigation;
